/** @file Results section of merchant domain whitelist */

import React, { useRef } from 'react';
import { Box, Stack, Typography, IconButton } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { SxProps } from '@mui/system';
import { Domain } from '../../../store/modules/merchant/types';
import { testId as parentTestId } from '../util';
import { useMerchant } from '../../../contexts/MerchantContext';
import { Button } from '../../../components/core/Button/Button';
import { DataTable, Column } from '../../../common/DataTable';

/** Local test ID */
export const testId = `${parentTestId}-RESULTS`;

interface Props {
  /** Existing domains */
  domains: Domain[] | undefined;
  /** Delete domain handler */
  onDelete: (domain: Domain) => void;
  /** Edit domain handler */
  onEdit: (domain: Domain) => void;
  /** Add domain handler */
  onAdd: () => void;
  /** Optional style overrides for base Box component */
  sx?: SxProps;
}

/**
 * Existing merchant domains section
 * @param {Props} props Component properties
 * @returns {JSX.Element} Results component with DataTable
 */
const Results = ({ domains, onAdd, onDelete, onEdit, sx = {} }: Props) => {
  const { canUpdateMerchantSettings } = useMerchant();
  const tableRef = useRef<HTMLDivElement>(null);
  const hasModifyAccess = canUpdateMerchantSettings();

  const columns: Column<Domain>[] = [
    {
      headerName: 'Domain URL',
      field: 'url',
      renderCell: (row: Domain) => <Typography>{row.url}</Typography>,
    },
    {
      id: 'actions',
      headerName: '',
      align: 'right',
      renderCell: (row: Domain) => {
        const rowIndex = domains?.findIndex((d) => d.id === row.id) ?? 0;

        return (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <IconButton
              onClick={() => onEdit(row)}
              data-testid={`${testId}-ROW_${rowIndex}-EDIT_BUTTON`}
              aria-label="edit domain"
              disabled={!hasModifyAccess}
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={() => onDelete(row)}
              data-testid={`${testId}-ROW_${rowIndex}-DELETE_BUTTON`}
              aria-label="delete domain"
              disabled={!hasModifyAccess}
            >
              <Delete />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ ...sx, width: '100%' }} data-testid={testId} component="section">
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 4,
          gap: 2,
        }}
      >
        <Stack>
          <Typography variant="large" fontWeight="bold">
            Merchant Domains List
          </Typography>
          <Typography variant="small" color="text.secondary">
            {`${domains?.length || 0} Merchant Domain${(domains?.length || 0) !== 1 ? 's' : ''}`}
          </Typography>
        </Stack>
        <Button
          theme="secondary"
          text="Add merchant domain"
          sx={{ px: 2, width: { xs: '100%', md: 'auto' } }}
          onClick={onAdd}
          disabled={!hasModifyAccess}
        />
      </Box>

      <DataTable
        tableRef={tableRef}
        rows={domains || []}
        columns={columns}
        getRowId={(row) => row.id}
        noRowsMessage="No merchant domains configured"
        tableHeight="auto"
      />
    </Box>
  );
};

export default Results;
